/**
 * FAQ section for checkout page
 */

import { useState } from 'react'
import { ChevronDown, HelpCircle } from 'lucide-react'
import { Button } from '@/components/ui'

interface FAQItem {
  question: string
  answer: string
}

interface CheckoutFAQProps {
  items?: FAQItem[]
}

const defaultFAQItems: FAQItem[] = [
  {
    question: 'How long will my order take?',
    answer:
      'Delivery orders typically arrive within 30-45 minutes. Pickup orders are usually ready in 15-20 minutes. You will receive an estimated time after placing your order.',
  },
  {
    question: 'Can I modify my order after placing it?',
    answer:
      'Please contact the restaurant directly as soon as possible if you need to modify your order. Changes may not be possible once the kitchen starts preparing your food.',
  },
  {
    question: 'What payment methods do you accept?',
    answer:
      'We accept cash, card on delivery/pickup, and online payments via credit/debit card. Choose your preferred method during checkout.',
  },
  {
    question: 'Do you deliver to my area?',
    answer:
      'Enter your delivery address on the pre-menu page to check if we deliver to your location. We will show available delivery options if your address is within our delivery zone.',
  },
  {
    question: 'Is there a minimum order amount?',
    answer:
      'Minimum order amounts may vary by location and order type. Any applicable minimums will be shown before you complete your order.',
  },
  {
    question: 'What if I have food allergies or dietary restrictions?',
    answer:
      'Please add any special instructions or dietary requirements in the delivery instructions field. You can also call the restaurant directly to discuss your needs.',
  },
]

function FAQAccordionItem({ item, isOpen, onToggle }: { item: FAQItem; isOpen: boolean; onToggle: () => void }) {
  return (
    <div className="border-b border-border">
      <Button
        variant="ghost"
        className="w-full flex items-center justify-between py-4 h-auto text-left hover:text-primary rounded-sm px-0 hover:bg-transparent"
        onClick={onToggle}
        aria-expanded={isOpen}
      >
        <span className="font-medium pr-4">{item.question}</span>
        <ChevronDown
          className={`h-5 w-5 flex-shrink-0 text-muted-foreground transition-transform ${
            isOpen ? 'rotate-180' : ''
          }`}
        />
      </Button>
      {isOpen && (
        <div className="pb-4 text-sm text-muted-foreground animate-fade-in">
          {item.answer}
        </div>
      )}
    </div>
  )
}

export function CheckoutFAQ({ items = defaultFAQItems }: CheckoutFAQProps) {
  const [openIndex, setOpenIndex] = useState<number | null>(null)

  const toggleItem = (index: number) => {
    setOpenIndex(openIndex === index ? null : index)
  }

  return (
    <section className="border-t pt-12 mt-12">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <HelpCircle className="h-6 w-6 text-primary" />
          <div>
            <h2 className="text-2xl font-bold">Frequently Asked Questions</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Find answers to common questions about ordering
            </p>
          </div>
        </div>

        {/* FAQ Accordion */}
        <div className="space-y-0">
          {items.map((item, index) => (
            <FAQAccordionItem
              key={index}
              item={item}
              isOpen={openIndex === index}
              onToggle={() => toggleItem(index)}
            />
          ))}
        </div>

        {/* Contact section */}
        <div className="mt-8 p-4 bg-muted rounded-lg text-center">
          <p className="text-sm text-muted-foreground">
            Still have questions?{' '}
            <Button variant="link" className="h-auto p-0 text-sm font-medium" asChild>
              <a href="tel:+15551234567">Call us</a>
            </Button>{' '}
            or{' '}
            <Button variant="link" className="h-auto p-0 text-sm font-medium" asChild>
              <a href="mailto:support@restaurant.com">send us an email</a>
            </Button>
          </p>
        </div>
      </div>
    </section>
  )
}
